import { useTheme } from '@emotion/react';
import { type ForwardedRef, forwardRef, type HTMLProps, memo, useMemo } from 'react';
import ReactPlayer from 'react-player/lazy';
import { useMedia } from 'react-use';

import { DEFAULT_VIDEO_ASPECT_RATIO, DEFAULT_VIDEO_WIDTH } from '@didaskill/domain/html/constants';

export type VideoProps = Omit<HTMLProps<HTMLElement>, 'ref'> & {
  src: string;
  caption?: string;
};

export const VideoBase = forwardRef(
  ({ src, caption = null, ...props }: VideoProps, ref: ForwardedRef<HTMLElement>) => {
    const { breakpoints } = useTheme();

    const isAboveSmall = useMedia(`(min-width: ${breakpoints.small})`);

    const playerWidth = useMemo(
      () => (isAboveSmall ? DEFAULT_VIDEO_WIDTH : DEFAULT_VIDEO_WIDTH * 0.5),
      [isAboveSmall],
    );

    return (
      <figure {...props} ref={ref}>
        <ReactPlayer
          url={src}
          width={playerWidth}
          height={playerWidth / DEFAULT_VIDEO_ASPECT_RATIO}
          controls
          light
        />

        {!!caption && <figcaption>{caption}</figcaption>}
      </figure>
    );
  },
);

VideoBase.displayName = 'Video';

export const Video = memo(VideoBase);
